import { gql, useApolloClient } from "@apollo/client";
import { cancelOrder } from "@repo/shopify/api/cancelOrder";
import { customerOrdersQuery } from "@repo/shopify/queries/customer-gql";
import { extractId } from "@repo/shopify/utils/common";
import { OrderStatusButtonType } from "@repo/shopify/utils/order";
import classNames from "classnames";
import React from "react";
import { Trans, useTranslation } from "react-i18next";

import Button from "@atoms/Button";
import Link from "@atoms/Link";

import { NOTIFICATIONS } from "@const/notifications";
import { URLS } from "@const/urls";

import useLanguage from "@hooks/useLanguage";

import { useCustomerStore } from "@store/customer";
import { useLayoutStore } from "@store/layout";

export default function OrderStatusButton({
  orderId,
  buttonType,
  className,
}: {
  orderId: string;
  buttonType: OrderStatusButtonType;
  className?: string;
}) {
  const { t } = useTranslation();
  const { lang } = useLanguage();
  const { openModal, openToast, closeAll, setIsLoading } = useLayoutStore();
  const { customer, token } = useCustomerStore();
  const client = useApolloClient();

  const refetchOrders = async () => {
    try {
      // 1. ROOT_QUERY에서 현재 로드된 주문 수 확인 (현재 OrderHistory에서 fetchMore로 로드된 총 주문 수)
      const cachedData = client.readQuery({
        query: gql(customerOrdersQuery.query),
        variables: {
          customerAccessToken: token.accessToken,
          first: 10, // OrderHistory의 초기값
          sortKey: "PROCESSED_AT",
          reverse: true,
        },
      });

      const currentTotalCount = cachedData?.customer?.orders?.edges?.length || 10;

      // 2. 서버에서 최신 데이터 가져오기 (취소된 주문 포함)
      const { data: freshData } = await client.query({
        query: gql(customerOrdersQuery.query),
        variables: {
          customerAccessToken: token.accessToken,
          first: currentTotalCount,
          sortKey: "PROCESSED_AT",
          reverse: true,
        },
        fetchPolicy: "no-cache",
      });

      // 3. 캐시 업데이트 (새로운 데이터로 기존 캐시 덮어쓰기 - fetchMore 구조 유지)
      client.writeQuery({
        query: gql(customerOrdersQuery.query),
        variables: {
          customerAccessToken: token.accessToken,
          first: 10,
          sortKey: "PROCESSED_AT",
          reverse: true,
        },
        data: freshData,
      });
    } catch (error) {
      // 실패하면 기본 refetch
      void client.refetchQueries({ include: [gql(customerOrdersQuery.query)] });
      const message = error instanceof Error ? error.message : String(error);
      throw new Error(message);
    }
  };

  const handleCancelOrder = async () => {
    try {
      setIsLoading(true);
      const response = await cancelOrder({
        shop: import.meta.env.VITE_REACT_APP_SHOP_NAME as string,
        customerId: extractId(customer.id),
        orderId,
        domain: lang,
      });

      if (response?.status === 200) {
        await refetchOrders();
        openToast(NOTIFICATIONS.SUCCESS_CANCEL_ORDER);
      } else {
        openToast(NOTIFICATIONS.ERROR_CANCEL_ORDER);
      }
    } catch (error) {
      openToast(NOTIFICATIONS.ERROR_CANCEL_ORDER);
      const message = error instanceof Error ? error.message : String(error);
      throw new Error(message);
    } finally {
      closeAll();
      setIsLoading(false);
    }
  };

  const handleOpenCancelModal = () => {
    openModal({
      title: "Cancel Order",
      content: <Trans i18nKey="modal.cancel_order.description" />,
      confirm: {
        label: "Confirm",
        onClick: () => {
          void handleCancelOrder();
        },
      },
      cancel: {
        label: "Cancel",
        onClick: () => {
          closeAll();
        },
      },
    });
  };

  return (
    <>
      {buttonType === OrderStatusButtonType.CANCEL && (
        <Button
          as="square"
          className={classNames("leading-none", className)}
          onClick={handleOpenCancelModal}
          variant="secondary"
        >
          {t("cancel_order")}
        </Button>
      )}

      {buttonType === OrderStatusButtonType.RETURN && (
        <Link
          as="square"
          className={classNames("flex! items-center justify-center px-3! leading-none", className)}
          href={`${URLS.RETURN_REQUEST_DETAIL}/${orderId}`}
          state={{ orderId }}
          variant="secondary"
        >
          {t("request_return")}
        </Link>
      )}
    </>
  );
}
